import { TFunction } from 'i18next';

interface ShareOffMarketWillingnessParams {
  buildingId: string;
  eventId: string;
  isSellInterest: boolean;
  address: {
    street?: string;
    houseNumber?: string;
    postcode?: string;
    city?: string;
  };
  t: TFunction;
  isMatch?: boolean;
}

/**
 * Shares an off-market buy or sell willingness event
 * @param params - The parameters needed for sharing
 * @returns Promise<boolean> - true if sharing was successful, false otherwise
 */
export const shareOffMarketWillingness = async ({
  buildingId,
  eventId,
  isSellInterest,
  address,
  t,
  isMatch
}: ShareOffMarketWillingnessParams): Promise<boolean> => {
  try {
    // Use backend share endpoint for social media crawlers to get proper meta tags
    // The backend will redirect regular users to the main app with the same parameters
    const shareUrl = `${process.env.REACT_APP_API_URL}/share-event?building-id=${buildingId}&event-id=${eventId}`;

    const shareTitle = isSellInterest
      ? t('building.offMarket.shareTitle.couldSell')
      : t('building.offMarket.shareTitle.couldBuy');

    // Build address string from components
    const addressParts = [];
    if (address.street && address.houseNumber) {
      addressParts.push(`${address.street} ${address.houseNumber}`);
    } else if (address.street) {
      addressParts.push(address.street);
    }

    if (address.postcode && address.city) {
      addressParts.push(`${address.postcode} ${address.city}`);
    } else if (address.city) {
      addressParts.push(address.city);
    }

    const addressString = addressParts.join(', ');
    const shareText = `${shareTitle} - ${addressString}`;

    // Try native sharing first
    if (navigator.share) {
      try {
        await navigator.share({
          title: shareTitle,
          text: shareText,
          url: shareUrl,
        });
        return true;
      } catch (error) {
        // User cancelled or error occurred
        console.error('Error sharing:', error);
        // Don't fall through to clipboard if user cancelled
        if (error instanceof Error && error.name === 'AbortError') {
          return false;
        }
      }
    }

    // Fallback to clipboard
    if (navigator.clipboard && navigator.clipboard.writeText) {
      try {
        await navigator.clipboard.writeText(shareUrl);
        alert(t('shareButton.linkCopied', 'Link copied to clipboard'));
        return true;
      } catch (err) {
        console.error('Could not copy text: ', err);
      }
    }

    // Fallback for older browsers
    const textArea = document.createElement('textarea');
    textArea.value = shareUrl;
    textArea.style.position = 'fixed';
    textArea.style.top = '0';
    textArea.style.left = '0';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    try {
      const successful = document.execCommand('copy');
      document.body.removeChild(textArea);

      if (successful) {
        alert(t('shareButton.linkCopied', 'Link copied to clipboard'));
        return true;
      } else {
        alert(t('shareButton.linkCopiedError', 'Could not copy link'));
        return false;
      }
    } catch (err) {
      console.error('Fallback: Unable to copy', err);
      document.body.removeChild(textArea);
      alert(t('shareButton.linkCopiedError', 'Could not copy link'));
      return false;
    }
  } catch (error) {
    console.error('Error in shareOffMarketWillingness:', error);
    return false;
  }
};

/**
 * Formats an address object into a readable string
 * @param address - The address object
 * @returns A formatted address string
 */
export const formatAddressString = (address: {
  street?: string;
  houseNumber?: string;
  postcode?: string;
  city?: string;
  country?: string;
}): string => {
  const parts = [];

  if (address.street) {
    parts.push(address.houseNumber ? `${address.street} ${address.houseNumber}` : address.street);
  }

  if (address.city) {
    parts.push(address.postcode ? `${address.postcode} ${address.city}` : address.city);
  }

  if (address.country) {
    parts.push(address.country);
  }

  return parts.join(', ');
};
